import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { prefs } from "./storage";

export type ThemePref = "system" | "light" | "dark";

const KEY = "atx-theme";
const ThemeContext = createContext<{ pref: ThemePref; setPref: (p: ThemePref) => void } | null>(null);

const systemDark = () => window.matchMedia("(prefers-color-scheme: dark)").matches;

function apply(pref: ThemePref) {
  const dark = pref === "dark" || (pref === "system" && systemDark());
  document.documentElement.classList.toggle("dark", dark);
  updateFavicon();
}

/** The favicon is drawn from the accent token at runtime, so no colour literal lives outside theme.css. */
function updateFavicon() {
  const link = document.getElementById("favicon");
  if (!(link instanceof HTMLLinkElement)) return;
  const accent = getComputedStyle(document.documentElement).getPropertyValue("--accent-core").trim();
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="${accent}" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M4 20v-4M10 20v-7M16 20v-4"/><path d="M4 11.5 10 8l4.5 2.5L21 3.5"/></svg>`;
  link.href = `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [pref, setPrefState] = useState<ThemePref>(() => {
    const saved = prefs.get(KEY);
    return saved === "light" || saved === "dark" ? saved : "system";
  });

  useEffect(() => {
    apply(pref);
    if (pref !== "system") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const on = () => apply("system");
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, [pref]);

  const setPref = useCallback((p: ThemePref) => {
    prefs.set(KEY, p);
    setPrefState(p);
  }, []);

  return <ThemeContext.Provider value={{ pref, setPref }}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme outside ThemeProvider");
  return ctx;
}
